import { ContentPlacement } from '@today/types'
import { Button } from '@today/ui-primitives'
import { type ReactElement, useRef } from 'react'
import { ContentConfirmDialogView } from './content-confirm-dialog.view'
import { SelectInput } from './content-fields'
import type { AssignmentRowView, ContentOption, PlacementView, Translate } from './content-types'
import { StatusChipView } from './status-chip.view'

/**
 * Props for {@link AssignmentListView}.
 *
 * @property rows - Assignments in their current order.
 * @property product - Product lists show placement, "Replaces" and per-language status.
 * @property replaceOptions - Per row id, the inherited texts it may replace.
 * @property canEdit - `content.assign`: reorder, placement/replaces and detach controls.
 * @property detaching - The assignment whose detach confirmation is open.
 * @property error - A translated reorder/update failure.
 */
export interface AssignmentListViewProps {
	t: Translate
	label: string
	rows: AssignmentRowView[]
	product: boolean
	replaceOptions: Record<string, ContentOption[]>
	canEdit: boolean
	detaching: { code: string; error: string | null } | null
	error: string | null
	isBusy: boolean
	onMove: (from: number, to: number) => void
	onPlacement: (id: string, placement: PlacementView) => void
	onReplaces: (id: string, textId: string) => void
	onDetach: (id: string) => void
	onOpen: (textId: string) => void
	onConfirmDetach: () => void
	onCancelDetach: () => void
}

const PLACEMENTS: PlacementView[] = Object.values(ContentPlacement)

function ProductFields({ props, row }: { props: AssignmentListViewProps; row: AssignmentRowView }): ReactElement {
	const { t } = props
	return (
		<>
			{props.product ? (
				<span className="flex flex-wrap gap-1">
					{row.chips.map((chip) => (
						<StatusChipView key={chip.locale} t={t} locale={chip.locale} label={chip.label} />
					))}
				</span>
			) : null}
			{props.product && row.placement ? (
				<SelectInput
					label={t('assignments.placement')}
					value={row.placement}
					disabled={!props.canEdit}
					options={PLACEMENTS.map((p) => ({ id: p, label: t(`placement.${p}`) }))}
					onChange={(v) => props.onPlacement(row.id, v as PlacementView)}
				/>
			) : null}
			{props.product ? (
				<SelectInput
					label={t('assignments.replaces')}
					value={row.replacesTextId ?? ''}
					disabled={!props.canEdit}
					emptyLabel={t('assignments.replacesNone')}
					options={props.replaceOptions[row.id] ?? []}
					onChange={(v) => props.onReplaces(row.id, v)}
				/>
			) : null}
		</>
	)
}

function Row({ props, row, index }: { props: AssignmentListViewProps; row: AssignmentRowView; index: number }) {
	const { t } = props
	const last = props.rows.length - 1
	return (
		<div className="flex flex-wrap items-end gap-3">
			<span className="font-medium text-ds-text">{row.code}</span>
			<span className="text-ds-sm text-ds-text-muted">{row.name}</span>
			<ProductFields props={props} row={row} />
			<Button size="sm" variant="muted" onPress={() => props.onOpen(row.textId)}>
				{t('assignments.open')}
			</Button>
			{props.canEdit ? (
				<>
					<Button
						size="sm"
						variant="muted"
						aria-label={t('assignments.moveUpLabel', { code: row.code })}
						isDisabled={props.isBusy || index === 0}
						onPress={() => props.onMove(index, index - 1)}
					>
						{t('assignments.moveUp')}
					</Button>
					<Button
						size="sm"
						variant="muted"
						aria-label={t('assignments.moveDownLabel', { code: row.code })}
						isDisabled={props.isBusy || index === last}
						onPress={() => props.onMove(index, index + 1)}
					>
						{t('assignments.moveDown')}
					</Button>
					<Button size="sm" variant="danger" onPress={() => props.onDetach(row.id)}>
						{t('assignments.detach')}
					</Button>
				</>
			) : null}
		</div>
	)
}

function Rows({ props }: { props: AssignmentListViewProps }): ReactElement {
	const dragged = useRef<number | null>(null)
	return (
		<ol className="flex flex-col gap-2">
			{props.rows.map((row, index) => (
				<li
					key={row.id}
					data-testid={`assignment-${row.code}`}
					draggable={props.canEdit}
					onDragStart={() => {
						// A read-only list never records a drag (a drag can start on a child element), and
						// no second move starts while one is saving — the same rule as the Move buttons.
						dragged.current = props.canEdit && !props.isBusy ? index : null
					}}
					onDragOver={(e) => e.preventDefault()}
					onDrop={(e) => {
						e.preventDefault()
						if (dragged.current !== null && dragged.current !== index) props.onMove(dragged.current, index)
						dragged.current = null
					}}
					className="rounded-ds-md border border-ds-border p-3"
				>
					<Row props={props} row={row} index={index} />
				</li>
			))}
		</ol>
	)
}

/** An ordered list of assignments with drag-and-drop and keyboard reorder (organism). */
export function AssignmentListView(props: AssignmentListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-2" aria-label={props.label}>
			<h2 className="text-ds-lg font-semibold text-ds-text">{props.label}</h2>
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.rows.length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">{t('assignments.empty')}</p>
			) : (
				<Rows props={props} />
			)}
			<ContentConfirmDialogView
				t={t}
				isOpen={props.detaching !== null}
				title={t('assignments.detachTitle')}
				body={t('assignments.detachBody', { code: props.detaching?.code ?? '' })}
				confirmLabel={t('assignments.detach')}
				danger
				isBusy={props.isBusy}
				error={props.detaching?.error}
				onConfirm={props.onConfirmDetach}
				onCancel={props.onCancelDetach}
			/>
		</section>
	)
}
